import { FC } from 'react';
import { Tabs } from '@dynamika-shared/ui/Tabs/Tabs';
import { Table } from '@dynamika-shared/ui/Table/Table';
import { SiteSubjectType, Site } from '@restore/ExternalUserSubjects/ExternalUserSubjects.types';
import { makeExternalUserSubjectListColumns } from './SubjectList.columns';
import { useMakePagination } from '@restore/paginationAndSort/paginationAndSort.slice';
import { useParams, useNavigate } from 'react-router-dom';
import { routesPath } from '@router/routes';
import { useSubjectListFilters } from './useSubjectListFilters';
import { useBreadcrumbs } from '@hooks/useBreadcrumbs';
import ReadingCompleted from '../ReadingCompleted/ReadingCompleted';
import { SubjectListHeader } from './components/SubjectListHeader';
import { SubjectSearchBar } from './components/SubjectSearchBar';
import { useSubjectListData } from './hooks/useSubjectListData';
import { ViewType } from '@restore/ExternalUserView/externalUserView.slice';

/**
 * External User Subject List Page
 *
 * Displays a list of subjects for external users with the following features:
 * - Site filter tabs (All, Site 001, Site 002, etc.)
 * - Search by Subject ID
 * - Sortable table columns (Last Uploaded Visit)
 * - Pagination
 * - Download action for each subject
 */

export const SubjectList: FC = () => {
  const { clinicalTrialId } = useParams<{ clinicalTrialId: string }>();
  const navigate = useNavigate();

  const { onSort, onSearch, sortBy } = useSubjectListFilters();

  const {
    state,
    context,
    searchValue,
    currentView,
    selectedSiteFilter,
    sites,
    handleSearchChange,
    handleClearSearch,
    handleTabChange,
  } = useSubjectListData({
    clinicalTrialId,
    onSearch,
  });

  const breadcrumbLabel = currentView === ViewType.EXTERNAL ? 'Subject List' : 'Readings Completed';

  useBreadcrumbs([[breadcrumbLabel, 'externalUserSubjectsClinicalTrial']], [clinicalTrialId, currentView]);

  const data = state?.data;
  const loading = Boolean(state?.loading);
  const totalElements = data?.totalElements ?? 0;
  const pagination = useMakePagination('externalUserSubjects', !!data?.totalElements);
  const clinicalTrialName = context?.clinicalTrialName;

  const handleRowClick = (subject: SiteSubjectType | number) => {
    if (typeof subject === 'number') return;
    // Navigate to subject detail page
    navigate(
      routesPath.externalUserSubjectDetailClinicalTrial
        .replace(':clinicalTrialId', clinicalTrialId!)
        .replace(':patientId', subject.id)
    );
  };

  const externalUserSubjectColumns = makeExternalUserSubjectListColumns(sites);

  // Prepare sites for tabs
  const tabsItems = sites.map((site: Site) => ({
    key: site.id,
    label: <span className='text-1624 font-medium'>{site.code || site.name}</span>,
    children: null,
  }));

  // Controlled by feature flag: externalUserViewSelector
  if (currentView === ViewType.SECONDARY) {
    return <ReadingCompleted />;
  }

  return (
    <>
      <SubjectListHeader
        clinicalTrialName={clinicalTrialName}
      />

      {tabsItems.length > 0 && (
        <div>
          <Tabs
            defaultActiveKey={selectedSiteFilter || sites[0]?.id}
            activeKey={selectedSiteFilter || sites[0]?.id}
            items={tabsItems}
            onChange={handleTabChange}
            blocked={false}
          />
        </div>
      )}

      <SubjectSearchBar
        totalElements={totalElements}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        onClearSearch={handleClearSearch}
      />

      <Table
        columns={externalUserSubjectColumns}
        data={data?.content || []}
        totalItems={data?.totalElements ?? 0}
        loading={loading}
        onSort={onSort}
        onRowClick={handleRowClick}
        loadingLength={8}
        sortState={sortBy}
        pagination={pagination}
      />
    </>
  );
};

export default SubjectList;
